import React, { useState, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import Buttons from '../../components/Button';
import { Vendor, Shop } from '../../interfaces/user';
import SearchInput from '../../components/SearchInput';
import {deleteShop} from '../../graphql/api/userApi';
import NoData from '../../components/NoData';

interface ShopManagementProps {
  vendors: Vendor[];
  shops: Shop[] | Shop[][];
  setShops: (shops: Shop[]) => void;
  setActiveTab: (tab: 'user' | 'vendor' | 'shop') => void;
  setIsEditing: (editing: boolean) => void;
  setShopForm: (form: any) => void;
  setSelections: (selections: any) => void;
}

const ShopManagement: React.FC<ShopManagementProps> = ({
  vendors,
  shops,
  setShops,
  setActiveTab,
  setIsEditing,
  setShopForm,
  setSelections
}) => {
  const [shopSearchText, setShopSearchText] = useState('');

 

 const flatShops: Shop[] = useMemo(() => shops.flat?.() ?? [], [shops]);
const flatVendors: Vendor[] = useMemo(() => vendors.flat?.() ?? [], [vendors]);

// console.log("Flat Shops:", flatShops);
// console.log("Flat Vendors:", flatVendors);

const filteredShops = useMemo(() => {
  const searchTerm = shopSearchText.toLowerCase();
  return flatShops.filter(shop => {
    const vendor = flatVendors.find(v => v.id === shop.vendorId);
    const locationStr = `${shop.location?.city ?? ''}, ${shop.location?.state ?? ''}, ${shop.location?.country ?? ''}`;
    return (
      (shop.shopName?.toLowerCase() ?? '').includes(searchTerm) ||
      locationStr.toLowerCase().includes(searchTerm) ||
      (shop.fromDate?.toLowerCase() ?? '').includes(searchTerm) ||
      (shop.toDate?.toLowerCase() ?? '').includes(searchTerm) ||
      (vendor?.businessName?.toLowerCase() ?? '').includes(searchTerm)
    );
  });
}, [flatShops, flatVendors, shopSearchText]);



  const handleShopEdit = (shop: Shop) => {
    setShopForm({
      id: shop.id,
      vendorId: shop.vendorId,
      shopName: shop.shopName,
      shopLogo: shop.shopLogo,
      location: shop.location,
      fromDate: shop.fromDate || '',
      toDate: shop.toDate || '',
    });
    setSelections({
      userId: "",
      vendorId: shop.vendorId,
      editingId: shop.id
    });
    setIsEditing(true);
    setActiveTab('shop');
  };



  const shopColumns = [
    {
  name: 'Image',
  cell: (row: Shop) =>
    row.shopLogo ? (
      <img
        src={row.shopLogo}
        alt={`${row.shopName} Logo`}
        style={{ width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover' }}
      />
    ) : (
      <span style={{ color: '#aaa' }}>No Logo</span>
    ),
  ignoreRowClick: true,
  allowOverflow: true,
  button: true,
},
    {
      name: 'Shop Name',
      selector: (row: Shop) => row.shopName,
      sortable: true,
    },
    {
      name: 'Address',
      cell: (row: Shop) =>
        `${row.location?.city ?? ''}, ${row.location?.state ?? ''}, ${row.location?.country ?? ''}`,
      sortable: true,
    },
    {
      name: 'From Date',
      selector: (row: Shop) => row.fromDate,
      sortable: true,
    },
    {
      name: 'To Date',
      selector: (row: Shop) => row.toDate,
      sortable: true,
    },
      {
      name: 'Associated Vendor',
      cell: (row: Shop) => {
        const vendor = flatVendors.find(v => v.id === row.vendorId); // ✅ use flatVendors
        return vendor?.businessName || 'Unknown';
      },
      sortable: true,
    },

    {
      name: 'Actions',
      cell: (row: Shop) => (
        <div className="action-buttons">
          <Buttons
            text="Edit"
            onClick={() => handleShopEdit(row)}
            variant="edit"
          />
          <Buttons
            text="Delete"
            onClick={() => deleteShop(row.id)}
            variant="delete"
          />
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div>
      <div className="mb-3">
         <SearchInput searchText={shopSearchText} setSearchText={setShopSearchText} />
      </div>

      <DataTable
        columns={shopColumns}
        data={filteredShops}
        pagination
        highlightOnHover
        striped
        responsive
        paginationPerPage={5}
        noDataComponent={<NoData message='No Shops found' />}
        customStyles={{
          headCells: {
            style: {
              fontWeight: 'bold',
              backgroundColor: '#f8f9fa',
            },
          },
          cells: {
            style: {
              padding: '12px',
            },
          },
        }}
      />
    </div>
  );
};

export default ShopManagement;
